import React, { useState, useEffect } from "react";
import axios from "axios";
import "./DailyPurchases.css";

const CadenceAchatsStats = () => {
  const [cadenceList, setCadenceList] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPeriodDays, setTotalPeriodDays] = useState(30);

  // Filtres
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [monthsBack, setMonthsBack] = useState(6);
  const [search, setSearch] = useState("");
  const [expandedArticle, setExpandedArticle] = useState(null);

  const fetchCadence = async () => {
    setLoading(true);
    try {
      const params = { monthsBack };
      if (selectedLocation !== "all") params.locationName = selectedLocation;

      let locList = [];
      try {
        const locRes = await axios.get("/api/Locations/getAllLocations");
        locList = Array.isArray(locRes.data) ? locRes.data : locRes.data.locations || [];
      } catch (e) {
        const locRes2 = await axios.get("/api/Locations");
        locList = Array.isArray(locRes2.data) ? locRes2.data : locRes2.data.locations || [];
      }
      setLocations(locList);

      const cadRes = await axios.get("/api/daily-purchases/cadence-stats", { params });
      setCadenceList(cadRes.data.cadenceList || []);
      setTotalPeriodDays(cadRes.data.totalPeriodDays || 30);
    } catch (err) {
      console.error("Erreur chargement cadence achats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCadence();
  }, [selectedLocation, monthsBack]);

  const filteredList = cadenceList.filter((art) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      art.articleName?.toLowerCase().includes(term) ||
      art.categoryName?.toLowerCase().includes(term) ||
      art.subCategoryName?.toLowerCase().includes(term) ||
      (art.locations || []).some((l) => l.locationName?.toLowerCase().includes(term))
    );
  });

  const totalAnalyzedSpend = filteredList.reduce((s, a) => s + (a.totalSpend || 0), 0);
  const totalAnalyzedVolume = filteredList.reduce((s, a) => s + (a.totalPurchasedQty || 0), 0);

  return (
    <div className="dp-card">
      <div className="dp-card-header">
        <div>
          <h3 className="dp-card-title">
            📈 Cadence d'Achat & Consommation Journalière par Article
          </h3>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
            Analyse des quantités consommées par jour, intervalles entre deux achats (en jours) et rythme de réapprovisionnement par emplacement.
          </p>
        </div>

        {/* Filtres */}
        <div className="dp-filters-bar">
          <select
            className="dp-filter-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="all">Tous les emplacements</option>
            {locations.map((loc, idx) => {
              const locName = typeof loc === "string" ? loc : (loc.name || loc.localisation || loc._id);
              const key = typeof loc === "object" && loc._id ? loc._id : `${locName}_${idx}`;
              return (
                <option key={key} value={locName}>
                  🏪 {locName}
                </option>
              );
            })}
          </select>

          <select
            className="dp-filter-select"
            value={monthsBack}
            onChange={(e) => setMonthsBack(Number(e.target.value))}
          >
            <option value={1}>1 dernier mois (30 jours)</option>
            <option value={3}>3 derniers mois (90 jours)</option>
            <option value={6}>6 derniers mois (180 jours)</option>
            <option value={12}>12 derniers mois (1 an)</option>
          </select>

          <input
            type="text"
            className="dp-filter-input"
            placeholder="🔍 Filtrer un article, emplacement..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="dp-btn dp-btn-secondary" onClick={fetchCadence} title="Actualiser">
            🔄 Actualiser
          </button>
        </div>
      </div>

      {/* Cartes Métriques Synthétiques */}
      <div className="dp-stats-grid">
        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#eff6ff", color: "#2563eb" }}>
            📦
          </div>
          <div className="dp-stat-info">
            <h4>Articles Distincts Analysés</h4>
            <div className="dp-stat-val">{filteredList.length}</div>
          </div>
        </div>

        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#ecfdf5", color: "#059669" }}>
            💰
          </div>
          <div className="dp-stat-info">
            <h4>Budget Total Consommé</h4>
            <div className="dp-stat-val" style={{ color: "#059669" }}>
              {totalAnalyzedSpend.toFixed(3)} DT
            </div>
          </div>
        </div>

        <div className="dp-stat-card">
          <div className="dp-stat-icon-wrapper" style={{ background: "#fef3c7", color: "#d97706" }}>
            📊
          </div>
          <div className="dp-stat-info">
            <h4>Volume Total Acheté</h4>
            <div className="dp-stat-val">{totalAnalyzedVolume} unités</div>
          </div>
        </div>
      </div>

      {/* Tableau d'Analyse de Cadence */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 font-weight-bold">Calcul précis de la cadence et de la consommation moyenne...</p>
        </div>
      ) : filteredList.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>
          <span style={{ fontSize: "3rem" }}>📈</span>
          <h5>Aucun historique disponible pour cette période</h5>
          <p>Les données de cadence apparaîtront au fur et à mesure des achats validés.</p>
        </div>
      ) : (
        <div className="dp-table-container">
          <table className="dp-table">
            <thead>
              <tr>
                <th>Article & Famille</th>
                <th style={{ textAlign: "center" }}>Consommation / Jour (Moyenne)</th>
                <th style={{ textAlign: "center" }}>Intervalle Réappro. (Jours)</th>
                <th style={{ textAlign: "center" }}>Volume Total Acheté</th>
                <th style={{ textAlign: "right" }}>Prix Moyen Unit.</th>
                <th style={{ textAlign: "right" }}>Total Dépensé (DT)</th>
                <th style={{ textAlign: "center", minWidth: "210px" }}>Détail par Emplacement</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((art, idx) => {
                const isExpanded = expandedArticle === idx;
                const locs = art.locations || [];

                let cadenceBadge = null;
                if (art.averageCadenceDays === null) {
                  cadenceBadge = (
                    <span className="dp-cadence-pill dp-cadence-slow">
                      1 seul achat (Ponctuel)
                    </span>
                  );
                } else if (art.averageCadenceDays <= 7) {
                  cadenceBadge = (
                    <span className="dp-cadence-pill dp-cadence-fast">
                      🔥 Tous les {art.averageCadenceDays} jours
                    </span>
                  );
                } else if (art.averageCadenceDays <= 20) {
                  cadenceBadge = (
                    <span className="dp-cadence-pill dp-cadence-medium">
                      ⚡ Tous les {art.averageCadenceDays} jours
                    </span>
                  );
                } else {
                  cadenceBadge = (
                    <span className="dp-cadence-pill dp-cadence-slow">
                      ⏱️ Tous les {art.averageCadenceDays} jours
                    </span>
                  );
                }

                return (
                  <React.Fragment key={idx}>
                    <tr style={{ background: isExpanded ? "#f8fafc" : "transparent" }}>
                      <td>
                        <div style={{ fontWeight: "800", color: "#0f172a", fontSize: "0.95rem" }}>
                          {art.articleName}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          📁 {art.categoryName} • {art.purchaseCount} commande(s)
                        </div>
                      </td>

                      {/* Consommation moyenne journalière globale */}
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            alignItems: "center",
                            background: "#ecfdf5",
                            border: "1.5px solid #a7f3d0",
                            borderRadius: "10px",
                            padding: "4px 12px"
                          }}
                        >
                          <span style={{ fontWeight: "900", color: "#065f46", fontSize: "0.95rem" }}>
                            ⚡ ~{art.dailyConsumptionRate} {art.unit} / jour
                          </span>
                          <span style={{ fontSize: "0.7rem", color: "#047857", fontWeight: "700" }}>
                            ({art.weeklyConsumptionRate} {art.unit} / semaine)
                          </span>
                        </div>
                      </td>

                      {/* Intervalle moyen entre achats */}
                      <td style={{ textAlign: "center" }}>
                        {cadenceBadge}
                      </td>

                      {/* Volume total */}
                      <td style={{ textAlign: "center" }}>
                        <span
                          style={{
                            fontWeight: "800",
                            background: "#f1f5f9",
                            padding: "4px 10px",
                            borderRadius: "8px",
                            fontSize: "0.88rem"
                          }}
                        >
                          {art.totalPurchasedQty} {art.unit}
                        </span>
                      </td>

                      {/* Prix moyen unitaire */}
                      <td style={{ textAlign: "right", fontWeight: "700", color: "#475569" }}>
                        {(art.averageUnitPrice || 0).toFixed(3)} DT
                      </td>

                      {/* Total Dépensé */}
                      <td style={{ textAlign: "right" }}>
                        <span style={{ fontWeight: "900", color: "#15803d", fontSize: "1rem" }}>
                          {(art.totalSpend || 0).toFixed(3)} DT
                        </span>
                      </td>

                      {/* Bouton détail par emplacement */}
                      <td style={{ textAlign: "center", minWidth: "210px", padding: "8px 12px" }}>
                        <button
                          type="button"
                          className="dp-btn dp-btn-primary"
                          style={{
                            padding: "8px 18px",
                            fontSize: "0.85rem",
                            fontWeight: "800",
                            whiteSpace: "nowrap",
                            minWidth: "180px",
                            width: "auto",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            boxShadow: "0 2px 5px rgba(37, 99, 235, 0.25)"
                          }}
                          onClick={() => setExpandedArticle(isExpanded ? null : idx)}
                        >
                          {isExpanded ? "🔼 Masquer Détails" : `🏪 ${locs.length} Emplacement${locs.length > 1 ? "s" : ""}`}
                        </button>
                      </td>
                    </tr>

                    {/* Ligne Dépliée : Détail de la Cadence par Emplacement */}
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan="7"
                          style={{
                            background: "#f1f5f9",
                            padding: "14px 20px",
                            borderLeft: "4px solid #2563eb"
                          }}
                        >
                          <div style={{ fontWeight: "800", color: "#0f172a", marginBottom: "8px", fontSize: "0.88rem" }}>
                            🏪 Consommation & Cadence d'Achat par Emplacement pour « {art.articleName} » :
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "10px" }}>
                            {locs.map((loc, lIdx) => (
                              <div
                                key={lIdx}
                                style={{
                                  background: "#ffffff",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: "10px",
                                  padding: "10px 14px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)"
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                  <span style={{ fontWeight: "800", color: "#1e293b", fontSize: "0.88rem" }}>
                                    📍 {loc.locationName}
                                  </span>
                                  <span style={{ fontSize: "0.75rem", background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px", fontWeight: "700" }}>
                                    {loc.purchaseCount} achat(s)
                                  </span>
                                </div>

                                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "6px 10px", marginBottom: "6px" }}>
                                  <div style={{ fontSize: "0.82rem", fontWeight: "800", color: "#15803d" }}>
                                    ⚡ Consommation : ~{loc.dailyConsumptionRate} {art.unit} / jour
                                  </div>
                                  <div style={{ fontSize: "0.72rem", color: "#166534" }}>
                                    Soit environ <strong>{loc.weeklyConsumptionRate} {art.unit}/semaine</strong> (~{loc.monthlyConsumptionRate} {art.unit}/mois)
                                  </div>
                                </div>

                                <div style={{ fontSize: "0.78rem", color: "#475569" }}>
                                  <div>
                                    ⏱️ <strong>Intervalle moyen :</strong> {loc.averageCadenceDays ? `Tous les ${loc.averageCadenceDays} jours` : "Achat ponctuel unique"}
                                  </div>
                                  <div>
                                    📦 <strong>Volume total :</strong> {loc.totalQty} {art.unit} ({loc.totalSpend.toFixed(3)} DT)
                                  </div>
                                  {loc.lastPurchaseDate && (
                                    <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: "2px" }}>
                                      Dernier achat : {new Date(loc.lastPurchaseDate).toLocaleDateString("fr-FR")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CadenceAchatsStats;

